/**
 * EVO — yon menyu konfiguratsiyasi.
 *
 * ============================================================================
 * MENYU KODDA EMAS, MA'LUMOTDA
 * ============================================================================
 *
 * Har bir band o'ziga kerakli ruxsatni O'ZI e'lon qiladi. Komponentda bironta
 * `if (role === 'sales')` yozilmaydi. Sabab:
 *
 *   1. Rol bo'yicha `if` — tuzoq. Bugun 5 rol, ertaga 7 ta bo'ladi va har
 *      ekranda unutilgan `if` qoladi. Ruxsat kodi esa matritsadan keladi.
 *   2. Yangi ekran qo'shgan dasturchi ruxsatni YOZMASDAN qo'sha olmaydi —
 *      `permission` maydoni majburiy (`null` yozish ham ongli qaror).
 *   3. Butun menyu bitta joyda ko'rinadi, ya'ni «kim nimani ko'radi» ni
 *      PERMISSION_MATRIX bilan solishtirish bir qarashda mumkin.
 *
 * ESLATMA: menyuni yashirish — XAVFSIZLIK EMAS. Haqiqiy himoya API'da
 * (`PermissionsGuard` + `maskFields`). Bu — ergonomika: sotuvchiga hech qachon
 * ochilmaydigan 20 ta band ko'rsatilmasin.
 *
 * Asos: EVO_PERMISSION_MATRIX_v1.md §3
 */
import type { NavIconName } from '../components/layout/nav-icons';
import type { TranslationKey } from '../lib/i18n';

/**
 * Ro'yxatga OLINGAN marshrutlar.
 *
 * ============================================================================
 * NIMA UCHUN ALOHIDA TIP
 * ============================================================================
 * Ilgari `MenuItem.to` oddiy `string` edi va `sidebar.tsx` da
 * `to={item.to as '/'}` kasti turardi — chunki menyudagi manzillarning
 * ko'pchiligi hali mavjud emas edi. Kast tipni O'CHIRIB QO'YARDI: menyudagi
 * xato manzil (`/produkts`) kompilyatsiyada ham, testda ham ushlanmasdi,
 * faqat foydalanuvchi 404 ko'rgandan keyin bilinardi.
 *
 * Endi tayyor ekranlar `route` maydonida TIPLANGAN literal bilan yoziladi:
 * `routes/index.tsx` da bunday marshrut bo'lmasa, `<Link to={...}>` chaqiruvi
 * KOMPILYATSIYA paytida yiqiladi.
 *
 * Hali yozilmagan ekranlar uchun `route: null`. Ular menyuda ko'rinadi
 * (fazalar rejasi ko'zga tashlanib tursin), lekin bosilmaydi — 404 ga olib
 * borishdan ko'ra «hali tayyor emas» deyish tushunarliroq.
 *
 * Yangi ekran qo'shilganda: `routes/index.tsx` ga marshrut yoziladi va shu
 * yerda `route: null` o'rniga manzil qo'yiladi. Ikkinchi qadamni unutish
 * mumkin emas — band bosilmay qolaveradi.
 */
export type MenuRoute =
  | '/'
  | '/products'
  /** Kategoriya, brend, o'lchov birligi (2026-08-15). */
  | '/reference'
  | '/customers'
  | '/suppliers'
  | '/invoices'
  | '/payments'
  | '/receivables'
  | '/payables'
  | '/fx-rates'
  | '/inventory/stock'
  | '/inventory/availability'
  | '/inventory/receipt'
  | '/inventory/issue'
  | '/inventory/transfers'
  | '/inventory/counts'
  | '/inventory/movements'
  /*
   * KIRUVCHI YUK — `/inventory/…` ostida EMAS va bu ongli.
   *
   * `/inventory/…` — bizning ichki ombor amallarimiz (kirim, chiqim,
   * ko'chirish, sanoq). Bu esa BOSHQA tashkilotdan keladigan hujjat: u
   * ombor daftariga faqat qabul qilingandan KEYIN tushadi. Manzil
   * bo'limning o'zi bilan bir xil nomlanadi (`supply`) — partner paketidagi
   * bo'lim kodi ham aynan shunday.
   */
  | '/supply/incoming'
  | '/sales/direct'
  | '/sales/my-cash'
  | '/sales/orders'
  | '/sales/deliveries'
  | '/sales/returns'
  | '/sales/returns/quarantine'
  | '/reports'
  | '/reports/stock'
  | '/reports/stock-valuation'
  | '/reports/sales'
  | '/reports/agent-activity'
  | '/reports/gross-profit'
  | '/reports/ar-aging'
  | '/reports/customer-discipline'
  | '/reports/cash-flow'
  | '/reports/dead-stock'
  | '/reports/shipment-profitability'
  | '/reports/reorder'
  // Tahlil hisobotlari (TZ v4.0 §28 / CR-007)
  | '/reports/abc'
  | '/reports/xyz'
  | '/reports/rfm'
  | '/reports/ltv'
  | '/reports/supplier-scorecard'
  | '/reports/container-efficiency'
  | '/reports/stock-age'
  | '/reports/customer-profitability'
  | '/reports/income-statement'
  | '/reports/balance-sheet'
  | '/reports/equity'
  | '/accounting/journals'
  | '/accounting/periods'
  | '/settings/accounts'
  | '/procurement/workspace'
  | '/procurement/orders'
  | '/procurement/reorder-runs'
  | '/procurement/shipments'
  | '/procurement/costing'
  | '/procurement/receipts'
  | '/procurement/supplier-invoices'
  | '/documents/expiring'
  | '/import'
  | '/settings/users'
  | '/settings/roles'
  | '/settings/price-lists'
  | '/settings/cash-accounts'
  | '/settings/cash-flow-items'
  | '/ai'
  | '/settings/print-templates'
  | '/accounting/trial-balance'
  | '/accounting/reconciliation'
  | '/settings/system'
  | '/settings/organization'
  /*
   * PARTNER TASHKILOTLAR — «Tashkiliy tuzilma» dan BOSHQA ekran.
   *
   * `/settings/organization` — BIZNING ichki tuzilmamiz (filiallar,
   * bo'limlar). Bu esa BOSHQA tashkilotlar: mijoz yoki diler shu dasturda
   * o'zi savdo qiladi va bizning ombor unga ko'rinmaydi. Nomlar yaqin,
   * ma'no esa teskari — shuning uchun ular yonma-yon turadi va ikkisi ham
   * o'z nomi bilan ataladi.
   *
   * Paket muharriri (`/settings/organizations/$id/modules`) bu ro'yxatda
   * YO'Q: u menyu bandi emas, satrdan ochiladigan ekran.
   */
  | '/settings/organizations'
  /*
   * XAVFSIZLIK — `/settings/audit` DAN KO'CHDI.
   *
   * Ekran Sozlamalar ostida ko'milgan edi va bu uni haftada bir marta
   * ochiladigan narsaga aylantirgan edi. Egasining talabi teskari: «barchasi
   * 1 joyda tursin — barcha holat nazoratda turishi uchun», ya'ni bo'lim
   * o'zi ko'rinib turishi kerak.
   *
   * Eski manzil O'CHIRILMADI, u YO'NALTIRUVCHIGA aylandi
   * (`routes/index.tsx`) — sabab o'sha yerda yozilgan.
   */
  | '/security'
  | '/approvals'
  | '/settings/approval-rules'
  | '/settings/delegations'
  | '/loyalty'
  | '/loyalty/point-rules'
  | '/loyalty/qr-batches'
  | '/loyalty/rewards'
  | '/loyalty/redemptions'
  | '/loyalty/installers'
  | '/loyalty/fraud'
  | '/dealers'
  | '/dealers/control'
  | '/bonus'
  | '/bonus/entries'
  | '/hr/employees'
  | '/hr/payroll'
  | '/hr/control'
  | '/reports/discounts'
  | '/finance/charity'
  | '/finance/credit-notes'
  | '/finance/treasury'
  | '/settings/me'
  | '/hr/commission'
  | '/hr/kpi'
  | '/finance/day-closing'
  | '/finance/expenses'
  | '/finance/supplier-payments'
  | '/finance/fx-revaluation';

export interface MenuItem {
  readonly id: string;
  readonly labelKey: TranslationKey;
  /** Rejalashtirilgan manzil — hujjat sifatida qoladi (marshrut hali bo'lmasa ham). */
  readonly to: string;
  /** Mavjud, tiplangan marshrut. `null` — ekran hali yozilmagan. */
  readonly route: MenuRoute | null;
  /**
   * Bandni ko'rish uchun kerakli ruxsat kodi.
   * `null` — hamma ko'radi (faqat bosh sahifa kabi neytral ekranlar uchun).
   */
  readonly permission: string | null;
  /**
   * MUQOBIL ruxsat: band `permission` YOKI shu kod bilan ko'rinadi.
   *
   * ┌────────────────────────────────────────────────────────────────────────┐
   * │ NIMA UCHUN QO'SHILDI VA NIMA UCHUN AYNAN BITTA KOD                     │
   * │                                                                        │
   * │ Nest tomonda ikki xil dekorator bor:                                   │
   * │   `@RequirePermissions(...)`   → AND                                   │
   * │   `@RequireAnyPermission(...)` → OR                                    │
   * │                                                                        │
   * │ AND uchun bu maydon kerak emas: to'plamdan ENG TOR kodni yozish        │
   * │ yetarli (`rpt-dead-stock` shunday qilingan). OR uchun esa YETARLI      │
   * │ EMAS — `suppliers.controller.ts` `supplier.view` YOKI                  │
   * │ `supplier.contact.view` ni qabul qiladi va bu ikki kod FARQLI          │
   * │ rollarda: moliyachida birinchisi bor va ikkinchisi yo'q, omborchida    │
   * │ teskarisi. Bitta kod yozsak, ochiq ekran ikki roldan birida menyudan   │
   * │ jimgina yo'qolardi — «menyu API dan QATTIQROQ» holati.                 │
   * │                                                                        │
   * │ Umumiy `readonly permissions: string[]` ro'yxati RAD ETILDI: u AND mi  │
   * │ yoki OR mi degan savolni har band uchun ochiq qoldirardi va bir kun    │
   * │ kimdir AND ma'nosida ishlatardi. Bitta ochiq nomlangan maydon esa      │
   * │ boshqacha o'qilishi mumkin emas.                                       │
   * └────────────────────────────────────────────────────────────────────────┘
   */
  readonly orPermission?: string;
  /**
   * Menyudan VAQTINCHA olib qo'yilgan band.
   *
   * Ruxsat bilan ARALASHTIRMANG: ruxsat «bu foydalanuvchiga tegishli emas»
   * deydi, bu esa «bu imkoniyat hozircha ishlatilmaydi» deydi — biznes
   * qarori, xavfsizlik emas. Ekran, marshrut va endpoint JOYIDA qoladi:
   * qaytarish uchun shu bitta so'zni o'chirish kifoya.
   *
   * Sabab har doim shu yerda, band yonida yoziladi — aks holda oradan
   * bir oy o'tib «nega bu yo'q?» degan savolga javob topilmaydi.
   */
  readonly hidden?: true;
  /**
   * Band FAQAT guruhda boshqa ko'rinadigan band bo'lsa qoladi.
   *
   * ┌────────────────────────────────────────────────────────────────────────┐
   * │ NIMA UCHUN «RUXSAT» BILAN HAL QILINMADI                                │
   * │                                                                        │
   * │ «Hisobotlar markazi» (`/reports`) o'zi hech qanday ma'lumot            │
   * │ ko'rsatmaydi — u shu guruhning QOLGAN bandlarini yig'ib beradi.        │
   * │ Demak uning ko'rinishi bitta ruxsat kodiga emas, «shu guruhda kamida   │
   * │ bitta hisobot ochiqmi?» degan savolga bog'liq.                         │
   * │                                                                        │
   * │ Bitta kod (`permission: 'stock.view'`) yozish oson yo'l edi va u xato  │
   * │ bo'lardi: `stock.view` yo'q, lekin `report.sales.view` bor xodimga     │
   * │ markaz ko'rinmay qolardi — aslida unda ochiladigan hisobot bor.        │
   * │ `permission: null` esa teskari xato: hech qanday hisobot ruxsati yo'q  │
   * │ xodim BO'SH sahifaga olib boradigan bandni ko'rardi.                   │
   * │                                                                        │
   * │ Shuning uchun qoida ruxsat emas, TARKIB: markaz yig'adigan narsa       │
   * │ bo'lsa turadi, bo'lmasa turmaydi.                                      │
   * └────────────────────────────────────────────────────────────────────────┘
   */
  readonly onlyWithSiblings?: true;
}

export interface MenuGroup {
  readonly id: string;
  readonly labelKey: TranslationKey;
  /**
   * Guruh darajasidagi darvoza.
   *
   * Nima uchun kerak: `sales` roli PERMISSION_MATRIX §3.5 bo'yicha o'z mijozining
   * qarzini (`ar.view` ◐) ko'radi. Agar filtr faqat band darajasida bo'lsa,
   * sotuvchining yon menyusida «Moliya» bo'limi paydo bo'lardi — talab esa
   * uni UMUMAN ko'rsatmaslik. Guruh darvozasi shu holatni hal qiladi:
   *
   *   Moliya  → `cashaccount.view`  (admin, manager, finance — sales/warehouse yo'q)
   *   Xarid   → `po.view`           (admin, manager, finance — sales yo'q)
   *
   * Sotuvchiga kerak bo'lgan «mening mijozim qarzi» ekrani Savdo guruhida turadi.
   */
  readonly permission: string | null;
  /**
   * Guruh nishoni — `components/layout/nav-icons.tsx` dagi kalit.
   *
   * Yon menyu yig'ilganda (guruhlar yopiq) foydalanuvchi bo'limni MATNDAN
   * emas, SHAKLDAN topadi: kuniga o'nlab marta ochiladigan menyuda ko'z
   * o'qimaydi, taniydi. Shuning uchun nishon majburiy maydon.
   */
  readonly icon: NavIconName;
  readonly items: readonly MenuItem[];
}

/*
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ TARTIB — ISH OQIMI BO'YICHA, ALIFBO YOKI TASODIF BILAN EMAS             ║
 * ║                                                                          ║
 * ║ Egasi: «bo'limlarni qayta tartibla, chalkashib ketgan».                   ║
 * ║                                                                          ║
 * ║ Ilgari «Ma'lumotnoma» ikkinchi o'rinda turardi — u esa oyda bir marta    ║
 * ║ ochiladi. «Kassa» va «Savdo» ikki alohida guruh edi. «Moliya» bilan      ║
 * ║ «Buxgalteriya» bir-biridan uzoqda edi.                                   ║
 * ║                                                                          ║
 * ║ Yangi tartib UCH QAVAT:                                                  ║
 * ║                                                                          ║
 * ║   1. HAR KUNI ochiladigan:  Bosh sahifa · Savdo · Ombor · Xarid          ║
 * ║   2. DAVRIY:                Moliya · Buxgalteriya · Hisobotlar · Usta    ║
 * ║   3. KAMDAN-KAM:            Ma'lumotnoma · Xodimlar · Tizim              ║
 * ║                                                                          ║
 * ║ Qoida: guruh qanchalik tez-tez ochilsa, shunchalik yuqorida turadi.      ║
 * ║ Yangi guruh qo'shilganda ham shu savolga javob berilsin — «buni kuniga   ║
 * ║ necha marta ochishadi?»                                                  ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export const MENU: readonly MenuGroup[] = [
  {
    id: 'general',
    labelKey: 'nav.group.general',
    permission: null,
    icon: 'home',
    items: [
      { id: 'dashboard', labelKey: 'nav.dashboard', to: '/', route: '/', permission: null },
      /*
       * ╔══════════════════════════════════════════════════════════════════╗
       * ║ «EVO'DAN SO'RASH» MENYUDAN OLIB TASHLANDI — YUQORI PANELDA       ║
       * ║                                                                  ║
       * ║ Egasi: «EVO'dan so'rash bo'lim qilib qo'yibsan bosh menyu        ║
       * ║ oldida — shuni qaysidir bo'limga yoki tepada o'ng tarafga        ║
       * ║ kichkina qilib qo'y».                                            ║
       * ║                                                                  ║
       * ║ Talab bilan MENYUNING O'Z QOIDASI bir joyga keldi. Bu yerdagi    ║
       * ║ har bir band — ISH BO'LIMI: sotuv buyurtmalari, ombor qoldig'i,  ║
       * ║ hisobotlar. «So'rash» esa bo'lim emas, ASBOB — u hech qanday     ║
       * ║ ma'lumot to'plamini ochmaydi, balki qolganlarining ustidan       ║
       * ║ savol beradi.                                                    ║
       * ║                                                                  ║
       * ║ Aynan shuning uchun uni «qaysidir bo'limga» qo'yish ham          ║
       * ║ ishlamasdi: savol omborga ham, qarzga ham, kadrlarga ham tegishli║
       * ║ bo'lishi mumkin, ya'ni istalgan bo'lim uni noto'g'ri toraytirardi║
       * ║                                                                  ║
       * ║ Yuqori panel esa aynan asboblar joyi — qidiruv (Ctrl+K) allaqachon║
       * ║ o'sha yerda va u ham xuddi shunday «har joy ustidan» ishlaydi.   ║
       * ║ Ruxsat `ai.ask` tekshiruvi YO'QOLMADI, u tugma bilan birga       ║
       * ║ `topbar.tsx` ga ko'chdi.                                         ║
       * ║                                                                  ║
       * ║ DIQQAT: endi `/ai` menyuda YO'Q, ya'ni yuqori paneldagi sarlavha ║
       * ║ uni menyudan topa olmaydi — ekranning O'ZI `usePageTitle` bilan  ║
       * ║ nomini e'lon qiladi (`routes/ai/ask.tsx`).                       ║
       * ╚══════════════════════════════════════════════════════════════════╝
       */
      /*
       * ╔══════════════════════════════════════════════════════════════════╗
       * ║ TASDIQLASH NAVBATI MENYUDAN OLIB TASHLANDI                       ║
       * ║                                                                  ║
       * ║ Egasi: «bosh menyu pastida "mening navbatim" bor, u kerak emas   ║
       * ║ menimcha, olib tashla».                                          ║
       * ║                                                                  ║
       * ║ Sabab amaliy: bugun bazada bironta tasdiqlash QOIDASI yo'q,      ║
       * ║ ya'ni navbat HAR DOIM bo'sh. Har kuni ochilib bo'sh chiqadigan   ║
       * ║ band menyuni shovqinga to'ldiradi va u bilan birga qolgan        ║
       * ║ bandlarga bo'lgan ishonch ham kamayadi.                          ║
       * ║                                                                  ║
       * ║ EKRAN VA ENDPOINT O'CHIRILMADI: `/approvals` marshruti ishlaydi  ║
       * ║ va hujjat kartochkasidagi tasdiqlash paneli ham joyida. Ya'ni    ║
       * ║ birinchi qoida yozilgan kuni navbat KERAK bo'ladi va o'shanda    ║
       * ║ bu qatorni qaytarish — bitta satr.                               ║
       * ║                                                                  ║
       * ║ Bandni qaytarish uchun quyidagi qatorni izohdan chiqaring:       ║
       * ║   { id: 'approvals', labelKey: 'nav.approvals',                  ║
       * ║     to: '/approvals', route: '/approvals',                       ║
       * ║     permission: 'approval.view' },                               ║
       * ╚══════════════════════════════════════════════════════════════════╝
       */
    ],
  },

  {
    id: 'sales',
    labelKey: 'nav.group.sales',
    permission: 'so.view',
    icon: 'sales',
    items: [
      /*
       * ╔══════════════════════════════════════════════════════════════════╗
       * ║ «KASSA» GURUHI «SAVDO» GA QO'SHILDI                              ║
       * ║                                                                  ║
       * ║ Egasi: «bo'limlarni qayta tartibla, chalkashib ketgan».           ║
       * ║                                                                  ║
       * ║ Ikkalasi ALOHIDA guruh edi va bu eng chalkash joyi: do'kon        ║
       * ║ savdosi ham SAVDO, faqat boshqa yo'l bilan. Sotuvchi «savdo       ║
       * ║ qilaman» deb «Savdo» ni ochardi va u yerda do'kon savdosini       ║
       * ║ TOPMASDI — u boshqa guruhda turardi.                             ║
       * ║                                                                  ║
       * ║ Endi hammasi bitta joyda va TARTIB ish oqimi bo'yicha:            ║
       * ║ tez savdo → mening kassam → buyurtma → yuk xati → vozvrat.        ║
       * ╚══════════════════════════════════════════════════════════════════╝
       */
      { id: 'direct-sale', labelKey: 'nav.directSale', to: '/sales/direct', route: '/sales/direct', permission: 'sale.direct' },
      /*
       * «Mening kassam» — `sale.direct`, `cashaccount.view` EMAS.
       *
       * Ekran kassa QOLDIG'INI ko'rsatmaydi: u sotuvchining O'Z savdolari
       * yig'indisi va server uni `salesperson_id = men` bo'yicha filtrlaydi.
       * `cashaccount.view` yozilsa band sotuvchiga UMUMAN ko'rinmasdi —
       * ya'ni ekran yozilib, egasiga yetib bormasdi.
       */
      { id: 'my-cash', labelKey: 'nav.myCash', to: '/sales/my-cash', route: '/sales/my-cash', permission: 'sale.direct' },
      // Yangi buyurtma ekrani (`/sales/orders/new`) menyuda ALOHIDA band
      // emas: u ro'yxatdagi tugmadan ochiladi va `so.create` talab qiladi
      // (omborchida `so.view` bor, `so.create` yo'q — unga tugma ko'rinmaydi).
      { id: 'sales-orders', labelKey: 'nav.salesOrders', to: '/sales/orders', route: '/sales/orders', permission: 'so.view' },
      /*
       * Yetkazishlar — `delivery.create`, `delivery.assign` EMAS.
       *
       * Band EKRANNI ochadi, ekran esa avval RO'YXATNI yuklaydi va
       * `GET /api/deliveries` aynan `delivery.create` talab qiladi
       * (`deliveries.controller.ts`). Biriktirish ruxsatini yozsak, menyu
       * API dan QATTIQROQ bo'lib qolardi — bugun ikkalasi ham bir xil uch
       * rolda (admin, rahbar, omborchi), lekin ular ajralgan kuni ochiq
       * ekran menyudan jimgina yo'qolardi.
       *
       * Guruh darvozasi `so.view`: omborchida u BOR (◐⁹ — yig'ishga tayyor
       * buyurtmalar), ya'ni band unga ko'rinadi. Moliyachida `so.view` bor,
       * lekin `delivery.create` YO'Q — unga band ko'rinmaydi va bu to'g'ri:
       * yuk xatini ombor chiqaradi (MATRIX §3.4, vazifalar ajratilishi).
       * Sotuvchi butun guruhni ko'radi, lekin bu bandni ko'rmaydi.
       *
       * JONLI TEKSHIRILGAN: `ombor@evo.uz` → `GET /api/deliveries` 200,
       * `GET /api/deliveries/drivers` 200, `GET /api/deliveries/cash-to-collect`
       * 403 (naqd pul paneli unga chizilmaydi).
       */
      { id: 'deliveries', labelKey: 'nav.deliveries', to: '/sales/deliveries', route: '/sales/deliveries', permission: 'delivery.create' },
      /*
       * HISOB-FAKTURALAR — `invoice.view`, kontrollerdagi bilan AYNAN bir xil.
       *
       * Band SAVDO guruhida, moliyada emas, va bu ongli: guruh darvozasi
       * `so.view` (sotuvchida BOR), moliya guruhiniki esa `cashaccount.view`
       * (sotuvchida YO'Q). Hisob-fakturani esa sotuvchi ko'rishi SHART —
       * o'z mijozining qarzi haqidagi savolga u javob beradi.
       *
       * Omborchida `so.view` bor, `invoice.view` yo'q — unga band ko'rinmaydi
       * va endpoint ham 403 beradi. JONLI TEKSHIRILGAN: `ombor@evo.uz` →
       * `GET /api/invoices` 403; `sotuvchi@evo.uz` → 200, lekin `total: 4`
       * (admin uchun 35) — cheklov SERVERDA, menyuda emas.
       */
      { id: 'invoices', labelKey: 'nav.invoices', to: '/invoices', route: '/invoices', permission: 'invoice.view' },
      /*
       * VOZVRAT — ruxsat `return.view`, `return.create` EMAS.
       *
       * Bandning vazifasi EKRANNI ochish, ekran esa avval RO'YXATNI yuklaydi
       * (`GET /api/returns` → `@RequirePermissions('return.view')`). Yaratish
       * tugmasi ekran ICHIDA `return.create` bilan tekshiriladi.
       *
       * Farq amaliy: bugungi matritsada `return.view` bor-u `return.create`
       * yo'q rol yo'q, lekin band ruxsati AYNAN kontrollerdagi kod bo'lishi
       * shart — ikkalasi bir kun ajralib ketadi va o'shanda band bosilganda
       * 403 chiqardi (`rpt-sales` bandidagi bir xil xato tarixi).
       *
       * Karantin — ALOHIDA band: bu kundalik ish ro'yxati va uni hujjatlar
       * ro'yxati ichiga ko'mib qo'ysak, omborchi har kuni ikki bosishdan
       * boshlardi. Ruxsat bir xil (`return.view`) — server ham shunday.
       */
      { id: 'returns', labelKey: 'nav.returns', to: '/sales/returns', route: '/sales/returns', permission: 'return.view' },
      { id: 'returns-quarantine', labelKey: 'nav.returnsQuarantine', to: '/sales/returns/quarantine', route: '/sales/returns/quarantine', permission: 'return.view' },
    ],
  },

  /*
   * ==========================================================================
   * USTA DASTURI — GURUH DARVOZASI `loyalty.view`
   * ==========================================================================
   * PERMISSION_MATRIX §3.8: `loyalty.view` da `warehouse` ham, `sales` ham
   * YO'Q. Bu ataylab va u TZ §60 talabi: `liability` hisoboti sovg'a
   * tannarxidan hosila qilingan ball kursini ko'rsatadi, ya'ni u XARAJAT
   * ma'lumoti. Sotuvchi tannarx ko'rmasligi kerak, omborchiga esa bu modul
   * umuman tegishli emas — stiker yopishtirishdan boshqa ishi yo'q.
   *
   * Guruh darvozasi shuning uchun BITTA kod: `loyalty.view` bo'lmasa butun
   * bo'lim menyudan tushib qoladi va sotuvchi «nimadir bor, lekin menga
   * yopiq» degan ma'lumotni ham olmaydi (`filterMenu` 1-qoidasi).
   *
   * Bandlar `loyalty.view` bilan ochiladi, «Nazorat navbati» esa
   * `loyalty.fraud.view` bilan — u tergov ma'lumoti (telefon, IP, urinish
   * tarixi) va matritsada alohida kod. Amaliyotda ikkalasi bir xil rollarda
   * (admin, rahbar, moliyachi), lekin nusxa AYNAN kontrollerdan olinadi:
   * menyu API dan qat'iyroq bo'lsa foydalanuvchi ochiq ekranni ko'rmay
   * qoladi (`nav.group.reports` izohi).
   *
   * YARATISH va TAHRIRLASH ruxsatlari (`loyalty.rules.edit`,
   * `loyalty.qr.generate`, `loyalty.reward.edit`, `loyalty.redemption.approve`,
   * `loyalty.installer.block`) menyuda YO'Q — ular EKRAN ICHIDA tekshiriladi.
   * Sabab omborchining «Ko'chirish» bandi bilan bir xil: bandning vazifasi
   * ekranni ochish, ekran esa avval RO'YXATNI yuklaydi va u `loyalty.view`
   * talab qiladi. Moliyachi qoidani o'zgartira olmaydi, lekin katalogni
   * KO'RISHI aynan uning ishi (jonli tekshirilgan: `moliya@evo.uz` uchun
   * `POST /loyalty/point-rules` va QR eksporti — 403, o'qish — 200).
   */
  {
    id: 'warehouse',
    labelKey: 'nav.group.warehouse',
    permission: 'stock.view',
    icon: 'warehouse',
    items: [
      // `sales` bu yerda faqat «bor/yo'q» ni ko'radi (§3.2 izoh⁵) — cheklov API tomonda.
      { id: 'stock', labelKey: 'nav.stock', to: '/inventory/stock', route: '/inventory/stock', permission: 'stock.view' },
      /*
       * «Qayerda bor?» — `stock` dan KEYIN, lekin ATAYLAB yonida.
       *
       * Ikkalasi ham qoldiq haqida, lekin boshqa savolga javob beradi:
       * `stock` — «mening omborimda nima bor» (pul bilan, ko'lam bilan),
       * `availability` — «bu tovar KOMPANIYADA qayerda bor» (pulsiz,
       * hamma filial). Ular ajratilgan bo'lsa odam ikkinchisini topmasdi.
       */
      { id: 'availability', labelKey: 'availability.title', to: '/inventory/availability', route: '/inventory/availability', permission: 'stock.view' },
      { id: 'movements', labelKey: 'nav.movements', to: '/inventory/movements', route: '/inventory/movements', permission: 'stock.view' },
      { id: 'receipts', labelKey: 'nav.receipts', to: '/inventory/receipt', route: '/inventory/receipt', permission: 'receipt.create' },
      { id: 'issue', labelKey: 'nav.issue', to: '/inventory/issue', route: '/inventory/issue', permission: 'receipt.create' },
      { id: 'transfers', labelKey: 'nav.transfers', to: '/inventory/transfers', route: '/inventory/transfers', permission: 'receipt.view' },
      { id: 'counts', labelKey: 'nav.counts', to: '/inventory/counts', route: '/inventory/counts', permission: 'receipt.view' },
    ],
  },

  /*
   * ╔══════════════════════════════════════════════════════════════════════╗
   * ║ TA'MINOT — O'Z GURUHI, «OMBOR» NING ICHIDA EMAS                      ║
   * ║                                                                      ║
   * ║ Band «Ombor» guruhiga qo'yilgan bo'lsa TABIIY ko'rinardi va bu XATO  ║
   * ║ bo'lardi: o'sha guruhning darvozasi `stock.view`, kiruvchi yukni     ║
   * ║ ko'rish esa `supply.notice.view` bilan ochiladi. Ikki kod har xil    ║
   * ║ rollarda bo'lishi mumkin va o'shanda ochiq ekran menyudan JIMGINA    ║
   * ║ yo'qolardi — «menyu API dan qattiqroq» holati (bu fayldagi eng ko'p  ║
   * ║ takrorlanadigan xato).                                               ║
   * ║                                                                      ║
   * ║ Guruh darvozasi bandning O'Z ruxsati bilan bir xil qilib qo'yilgan.  ║
   * ║ Odatda bu yaxshi belgi emas (guruhda bitta band), lekin bu yerda u   ║
   * ║ AYNAN to'g'ri: ta'minot ko'prigi butun bir bo'lim bo'lib ochiladi    ║
   * ║ yoki umuman ochilmaydi — partner paketida u `supply` degan bitta     ║
   * ║ bo'lim.                                                              ║
   * ║                                                                      ║
   * ║ ⚠️ ADMINDA BU GURUH KO'RINADI — o'lchangan, taxmin qilinmagan.        ║
   * ║                                                                      ║
   * ║ Avval bu yerda «bizning xodimlarimizda `supply.notice.view` yo'q,    ║
   * ║ ya'ni guruh ularga ko'rinmaydi» deb yozilgan edi. Bu YOLG'ON: admin  ║
   * ║ roli katalogdagi BARCHA kodlarni oladi (`seed/permissions.ts`), ya'ni║
   * ║ bu kod unda ham bor. `ui:smoke` o'lchab ko'rsatdi — jami ekranlar    ║
   * ║ 316 dan 317 ga chiqdi va o'sish AYNAN adminda (95 → 96).             ║
   * ║                                                                      ║
   * ║ Bu nuqson emas: admin hamma narsani ko'radi, shu jumladan bizda      ║
   * ║ hech qachon to'lmaydigan ro'yxatni ham. Qolgan to'rt rolda (rahbar,  ║
   * ║ moliya, ombor, sotuvchi) guruh KO'RINMAYDI — ularning ekran soni     ║
   * ║ o'zgarmadi (89, 83, 27, 22).                                          ║
   * ║                                                                      ║
   * ║ Guruhning asl uyi — shu dasturda O'ZI savdo qiladigan partner        ║
   * ║ tashkilot: u yerda ro'yxat to'ladi va bo'lim kundalik ishga kiradi.  ║
   * ║                                                                      ║
   * ║ O'rni «Ombor» dan KEYIN: yuk avval keladi, keyin qoldiqqa aylanadi.  ║
   * ╚══════════════════════════════════════════════════════════════════════╝
   */
  {
    id: 'supply',
    labelKey: 'nav.group.supply',
    permission: 'supply.notice.view',
    icon: 'warehouse',
    items: [
      /*
       * Ruxsat — `supply.notice.view`, `supply.notice.receive` EMAS.
       *
       * Band EKRANNI ochadi, ekran esa avval RO'YXATNI yuklaydi va
       * `GET /api/partner-supply` aynan ko'rish kodini talab qiladi.
       * Qabul qilish tugmasi EKRAN ICHIDA alohida tekshiriladi va ruxsat
       * bo'lmasa DOM da ham qolmaydi: kirim ombor qoldig'ini va o'rtacha
       * tannarxni o'zgartiradi, ya'ni uni kuzatuvchi (buxgalter) emas,
       * omborchi bosadi. Kuzatuvchi esa kelayotgan yukni KO'RISHI kerak.
       */
      { id: 'incoming-supply', labelKey: 'nav.incomingSupply', to: '/supply/incoming', route: '/supply/incoming', permission: 'supply.notice.view' },
    ],
  },

  {
    id: 'procurement',
    labelKey: 'nav.group.procurement',
    // Guruh darvozasi — `sales` roli butun bo'limni ko'rmaydi.
    permission: 'po.view',
    icon: 'import',
    /*
     * «Tannarx hisobi» ALOHIDA BAND EMAS va bo'lishi ham mumkin emas: u
     * konkret konteynerni talab qiladi (`/reports/landed-cost/$shipmentId`
     * bilan bir xil sabab). Kirish nuqtasi bitta — konteyner kartochkasidagi
     * uchinchi tab, u `landedcost.view` bilan ochiladi.
     *
     * «Tovar qabuli» esa alohida band: omborchi kuniga bir necha marta
     * kiradi va u konteyner kartochkasi ichida ko'milib qolmasligi kerak.
     * Ruxsat `receipt.view` — omborchida u BOR, sotuvchida yo'q.
     */
    items: [
      /*
       * ISH MAYDONI — guruhning BIRINCHI bandi.
       *
       * Tartib ataylab: ta'minot xodimi kunni shu ekrandan boshlaydi
       * (nima shoshilinch, qachon qancha to'lash kerak), buyurtmalar
       * ro'yxatiga esa keyin — aniq hujjat kerak bo'lganda kiradi.
       *
       * Ruxsat `po.view` — AYNAN kontrollerdagi kod
       * (`procurement-workspace.controller.ts` → `@RequirePermissions
       * ('po.view')`). Xizmat qatlami QO'SHIMCHA `supplier.view` talab
       * qiladi, lekin menyu ruxsatlar jadvalining ikkinchi nusxasiga
       * aylanmasligi kerak — bu qoida `supplier-invoices` bandida
       * batafsil yozilgan. Amalda: omborchi bandni ko'radi, ochganda
       * sabab bilan 403 oladi.
       */
      { id: 'procurement-workspace', labelKey: 'nav.procurementWorkspace', to: '/procurement/workspace', route: '/procurement/workspace', permission: 'po.view' },
      { id: 'purchase-orders', labelKey: 'nav.purchaseOrders', to: '/procurement/orders', route: '/procurement/orders', permission: 'po.view' },
      /*
       * Avtomatik qayta zakaz — `reorder.view`, `po.view` EMAS.
       *
       * Guruh darvozasi allaqachon `po.view`, ya'ni bu yerga faqat xarid
       * ko'radigan rol yetib keladi. Band ruxsati esa AYNAN kontrollerdagi
       * kod bo'lishi shart (`reorder.controller.ts` → `@RequirePermissions
       * ('reorder.view')`): `po.view` yozsak, ikkalasi bir kun ajralib
       * ketadi va band bosilganda 403 chiqadi.
       */
      { id: 'reorder-runs', labelKey: 'nav.reorderRuns', to: '/procurement/reorder-runs', route: '/procurement/reorder-runs', permission: 'reorder.view' },
      { id: 'shipments', labelKey: 'nav.shipments', to: '/procurement/shipments', route: '/procurement/shipments', permission: 'shipment.view' },
      /*
       * Tannarx bo'limi — `landedcost.view`, `shipment.view` EMAS.
       *
       * Farq amaliy va u AYNAN shu ekran uchun hal qiluvchi: sotuvchida
       * `shipment.view` BOR (u ETA ni ko'radi, MATRIX §3.3 izoh⁷), ya'ni
       * `shipment.view` yozilsa band unga KO'RINARDI va bosganda 403
       * chiqardi. Kod kontrollerdagi bilan bir xil
       * (`costing-workspace.controller.ts` → `@RequirePermissions('landedcost.view')`).
       *
       * Guruh darajasidagi `po.view` ham qo'shimcha to'siq bo'lib qoladi:
       * sotuvchi butun bo'limni ko'rmaydi.
       */
      { id: 'costing', labelKey: 'nav.costing', to: '/procurement/costing', route: '/procurement/costing', permission: 'landedcost.view' },
      { id: 'goods-receipts', labelKey: 'nav.goodsReceipts', to: '/procurement/receipts', route: '/procurement/receipts', permission: 'receipt.view' },
      /*
       * Ta'minotchi invoysi — `po.view`, `ap.view` EMAS.
       *
       * Ekran narx ma'lumotini ko'rsatadi va xizmat qatlami QO'SHIMCHA
       * `ap.view` ham talab qiladi. Lekin band ruxsati AYNAN kontrollerdagi
       * kod bo'lishi shart (`supplier-invoices.controller.ts` →
       * `@RequirePermissions('po.view')`), aks holda ikkalasi bir kun
       * ajralib ketadi — bu qoida yuqoridagi `reorder-runs` izohida
       * batafsil yozilgan.
       *
       * Amalda ikkala kod ham bir xil uch rolda (`admin`/`manager`/
       * `finance`), ya'ni bugun farq ko'rinmaydi. Omborchida esa `po.view`
       * bor (◐⁶ — narxsiz kutilma ro'yxati) va `ap.view` yo'q: u bandni
       * KO'RADI, ochganda esa 403 oladi. Bu ONGLI: menyu ruxsatlar
       * jadvalining ikkinchi nusxasiga aylanmasligi kerak.
       */
      { id: 'supplier-invoices', labelKey: 'nav.supplierInvoices', to: '/procurement/supplier-invoices', route: '/procurement/supplier-invoices', permission: 'po.view' },
      /*
       * Muddati tugayotgan hujjatlar — NEGA AYNAN XARID BO'LIMIDA.
       *
       * Ruxsat (`document.view`) hamma rolda, ya'ni bandni istalgan
       * guruhga qo'yish mumkin edi. XARID tanlandi, chunki muammoning
       * O'ZI shu yerda: muddati o'tgan sertifikat bilan bojxonadan o'tib
       * bo'lmaydi va konteyner portda turib qoladi. Ro'yxatga qaraydigan
       * odam — konteynerni kuzatayotgan odam.
       *
       * «Sozlamalar» guruhi RAD ETILDI: u `audit.view` bilan yopilgan
       * (admin, rahbar, moliyachi) va omborchi ro'yxatni umuman
       * ko'rmasdi. Guruh darvozasi bu yerda `po.view` — u omborchida BOR.
       */
      { id: 'documents-expiring', labelKey: 'nav.documentsExpiring', to: '/documents/expiring', route: '/documents/expiring', permission: 'document.view' },
      /*
       * ╔══════════════════════════════════════════════════════════════════╗
       * ║ FAYLDAN YUKLASH — MENYUGA QAYTARILDI (2026-08-15)                ║
       * ║                                                                  ║
       * ║ Bu band ilgari `hidden: true` edi va sabab yozilgan edi: «c1      ║
       * ║ malumot hozircha kerak emas, bu bo'lim hali umuman               ║
       * ║ ishlatmasligimiz mumkin».                                        ║
       * ║                                                                  ║
       * ║ QAROR O'ZGARDI. Egasi endi teskarisini so'radi: «1000 ta mahsulot ║
       * ║ bo'lsa bittalab kiritish kerak emasku, u bo'limga faylda yuklash  ║
       * ║ bo'lishi kerak». Ya'ni bo'lim endi FAQAT 1C ko'chirishi uchun     ║
       * ║ emas — u katalogni, mijozlarni va narxlarni ommaviy kiritishning  ║
       * ║ yagona yo'li.                                                     ║
       * ║                                                                  ║
       * ║ Yorliq ham shunga qarab o'zgardi: «1C dan import» emas, «Fayldan  ║
       * ║ yuklash» — bo'lim 1C ga bog'liq emas, u ISTALGAN Excel faylni     ║
       * ║ oladi.                                                            ║
       * ║                                                                  ║
       * ║ Ruxsat O'ZGARMADI (`migration.run`, faqat adminda): bu ekran      ║
       * ║ bazaga to'g'ridan-to'g'ri ming qator yozadi va uni ochish         ║
       * ║ ALOHIDA qaror. Menejerga berish kerak bo'lsa — bu ruxsat          ║
       * ║ masalasi, menyu masalasi emas.                                    ║
       * ╚══════════════════════════════════════════════════════════════════╝
       */
      { id: 'import', labelKey: 'nav.import', to: '/import', route: '/import', permission: 'migration.run' },
    ],
  },

  /*
   * ╔══════════════════════════════════════════════════════════════════════╗
   * ║ KASSA — SAVDO GURUHIDAN OLDIN, VA BU ATAYLAB                         ║
   * ║                                                                      ║
   * ║ Sotuvchi kuniga o'nlab marta AYNAN shu ikkita ekranni ochadi:        ║
   * ║ do'konda mijoz turibdi, savdo bir daqiqada bo'lib o'tadi. Buyurtma   ║
   * ║ ro'yxati esa kunda bir-ikki marta ochiladi. Menyuda pastda turgan    ║
   * ║ eng ko'p ishlatiladigan band — har kuni takrorlanadigan qidiruv.     ║
   * ║                                                                      ║
   * ║ Guruh darvozasi `sale.direct`: bu kod admin, rahbar va sotuvchida    ║
   * ║ bor. Omborchi, moliyachi va dastavkachida YO'Q — ular do'konda       ║
   * ║ savdo qilmaydi va ularga bu guruh umuman ko'rinmaydi.                ║
   * ╚══════════════════════════════════════════════════════════════════════╝
   */
  /*
   * ╔══════════════════════════════════════════════════════════════════════╗
   * ║ FILIAL KASSASI — ALOHIDA GURUH, «MOLIYA» NING ICHIDA EMAS            ║
   * ║                                                                      ║
   * ║ Sabab bitta va u qat'iy: «Moliya» guruhining darvozasi               ║
   * ║ `cashaccount.view` va u SOTUVCHIDA YO'Q — ya'ni filial kassiri butun ║
   * ║ bo'limni ko'rmaydi. Kun yopish bandini o'sha guruhga qo'ysak, u      ║
   * ║ o'zining YAGONA foydalanuvchisiga ko'rinmasdi.                       ║
   * ║                                                                      ║
   * ║ Darvozani `cashaccount.view` dan kengaytirish MUMKIN EMAS: u atayin  ║
   * ║ tor qo'yilgan (sotuvchi kompaniyaning pul pozitsiyasini ko'rmaydi).  ║
   * ║ Ya'ni bandni «Moliya» ga tiqishtirish ikki yomon variantdan birini   ║
   * ║ tanlashni talab qilardi — uchinchisi tanlandi: o'z guruhi.           ║
   * ║                                                                      ║
   * ║ Darvoza `dayclosing.view`: kassir, rahbar, moliyachi va admin        ║
   * ║ ko'radi; omborchi va dastavkachida bu kod umuman yo'q.               ║
   * ║                                                                      ║
   * ║ Guruh «Moliya» dan OLDIN turadi va bu ham ataylab: kassir uni har    ║
   * ║ kuni ochadi, moliyachi esa hisobotlarni haftada bir marta.           ║
   * ╚══════════════════════════════════════════════════════════════════════╝
   */
  {
    id: 'branch-cash',
    labelKey: 'nav.group.branchCash',
    permission: 'dayclosing.view',
    icon: 'finance',
    items: [
      { id: 'day-closing', labelKey: 'nav.dayClosing', to: '/finance/day-closing', route: '/finance/day-closing', permission: 'dayclosing.view' },
      /*
       * `expense.view`, `expense.create` EMAS.
       *
       * Rahbarda va moliyachida ikkalasi ham bor, lekin kelajakda faqat
       * ko'ruvchi rol paydo bo'lsa, band unga ham ochiq qolishi kerak:
       * ekran ichida yaratish tugmasi `expense.create` bilan alohida
       * tekshiriladi (`payments` bandidagi bir xil mulohaza).
       */
      { id: 'expenses', labelKey: 'nav.expenses', to: '/finance/expenses', route: '/finance/expenses', permission: 'expense.view' },
    ],
  },

  {
    id: 'finance',
    labelKey: 'nav.group.finance',
    // Guruh darvozasi — `sales` va `warehouse` butun bo'limni ko'rmaydi.
    permission: 'cashaccount.view',
    icon: 'finance',
    items: [
      /*
       * ========================================================================
       * TO'LOVLAR — `payment.view`, YARATISH/POST EMAS
       * ========================================================================
       * Band EKRANNI ochadi, ekran esa avval RO'YXATNI yuklaydi
       * (`GET /api/payments` → `@RequirePermissions('payment.view')`).
       * Yaratish (`payment.create`) va post qilish (`payment.post`) tugmalari
       * EKRAN ICHIDA tekshiriladi va ruxsat bo'lmasa DOM da ham qolmaydi.
       *
       * Farq amaliy: rahbarda `payment.view` BOR, `payment.create` YO'Q
       * (MATRIX §3.5 — pul harakati vazifalar ajratilishining eng qat'iy
       * joyi). Bandni `payment.create` ga bog'lasak, rahbar to'lovlarni
       * umuman ko'ra olmasdi.
       *
       * SOTUVCHI holati ALOHIDA va u ongli chetlab o'tish emas: unda
       * `payment.view` ◐¹² BOR (o'z mijozining to'lov FAKTI), lekin guruh
       * darvozasi `cashaccount.view` uni butun «Moliya» bo'limidan chiqarib
       * yuboradi. Sotuvchi to'lov faktini HISOB-FAKTURA kartochkasida
       * ko'radi (`/invoices?id=…` dagi «Mijozning to'lovlari» bloki) — u
       * Savdo guruhida va o'sha blok aynan `payment.view` bilan chiziladi.
       * JONLI TEKSHIRILGAN: `ombor@evo.uz` → `GET /api/payments` 403;
       * `sotuvchi@evo.uz` → 200 (`total: 0`, chunki ko'lamida to'lov yo'q).
       */
      /*
       * G'AZNACHILIK — Moliya guruhining BIRINCHI bandi.
       *
       * «Pulim qachon tugaydi» degan savol qolgan hammasidan oldin
       * keladi: to'lovlar, qarzlar va kassalar shu savolga javob
       * beradigan tafsilotlar.
       *
       * Serverda o'n bitta endpoint bor edi va birortasi ham dasturdan
       * chaqirilmasdi — ya'ni javob yozilgan, ekran esa yo'q edi.
       */
      { id: 'treasury', labelKey: 'nav.treasury', to: '/finance/treasury', route: '/finance/treasury', permission: 'treasury.view' },
      { id: 'payments', labelKey: 'nav.payments', to: '/payments', route: '/payments', permission: 'payment.view' },
      /*
       * MIJOZ QARZLARI — `ar.view`, endpointlar bilan bir xil
       * (`GET /reports/ar-aging` va `GET /customers/:id/balance`).
       *
       * Sotuvchida `ar.view` ◐ BOR, lekin bu band unga ko'rinmaydi (guruh
       * darvozasi). Uning kirish nuqtasi — Hisobotlardagi «Mijoz qarzi»
       * (`/reports/ar-aging`, guruh darvozasi `null`). Ikkita ekran bir xil
       * endpointdan o'qiydi va bu takror emas: bu yerdagisi MIJOZ KESIMIDA
       * ishlaydigan ro'yxat (kartochka, ochiq hisob-fakturalar, undirish),
       * hisobot esa eksport qilinadigan kesim.
       */
      { id: 'receivables', labelKey: 'nav.receivables', to: '/receivables', route: '/receivables', permission: 'ar.view' },
      /*
       * KREDIT-NOTA — mijoz qarzi YONIDA.
       *
       * Serverda sakkizta endpoint bor edi, dasturda esa uni ochadigan
       * joy yo'q: tovar qaytadi, qarz esa qolaveradi. Bu ekran o'sha
       * teshikni yopadi va aynan qarz ro'yxati yonida turadi — chunki
       * u qarzni KAMAYTIRADIGAN hujjat.
       */
      { id: 'credit-notes', labelKey: 'nav.creditNotes', to: '/finance/credit-notes', route: '/finance/credit-notes', permission: 'creditnote.view' },
      { id: 'payables', labelKey: 'nav.payables', to: '/payables', route: '/payables', permission: 'ap.view' },
      /*
       * ========================================================================
       * TA'MINOTCHIGA TO'LOV — `ap.view`, `payment.supplier` EMAS
       * ========================================================================
       * Band EKRANNI ochadi, ekran esa avval OCHIQ QARZLARNI yuklaydi
       * (`GET /api/payments/supplier/debts` → `@RequirePermissions('ap.view')`).
       * To'lash tugmasi EKRAN ICHIDA `payment.supplier` bilan tekshiriladi va
       * ruxsat bo'lmasa DOM da ham qolmaydi — `payments` bandidagi bir xil
       * mulohaza va bir xil sabab.
       *
       * Farq amaliy: rahbarda `ap.view` BOR, `payment.supplier` esa YO'Q
       * (MATRIX §3.5 — «Xitoy zavodiga o'tkazma pul harakatining eng yirik
       * summali holati»). Bandni `payment.supplier` ga bog'lasak, rahbar
       * qaysi invoys muddati o'tganini umuman ko'ra olmasdi.
       *
       * Bandning o'rni `payables` DAN KEYIN: hisobot «kimga qancha qarzmiz»
       * degan savolga javob beradi, bu esa keyingi qadam — «to'laymiz».
       */
      { id: 'supplier-payments', labelKey: 'nav.supplierPayments', to: '/finance/supplier-payments', route: '/finance/supplier-payments', permission: 'ap.view' },
      { id: 'cash-accounts', labelKey: 'nav.cashAccounts', to: '/settings/cash-accounts', route: '/settings/cash-accounts', permission: 'cashaccount.view' },
      /*
       * DDS maqolalari — kassa hisoblaridan KEYIN va bu ataylab.
       *
       * Hisob «pul qayerda turibdi» degan savolga, maqola esa «pul nima
       * uchun harakat qildi» degan savolga javob beradi. Ikkinchisi
       * birinchisisiz ma'nosiz: avval hisob ochiladi, keyin unga
       * yoziladigan harakat turkumlanadi.
       */
      { id: 'cash-flow-items', labelKey: 'nav.cashFlowItems', to: '/settings/cash-flow-items', route: '/settings/cash-flow-items', permission: 'treasury.view' },
      /*
       * ╔══════════════════════════════════════════════════════════════════════╗
       * ║ EHSON FONDI — MOLIYADA, BUXGALTERIYADA EMAS                          ║
       * ║                                                                      ║
       * ║ Avval Buxgalteriya guruhiga qo'yilgan edi va mantiq to'g'ri edi:     ║
       * ║ fond — foydadan ajratma va balansdagi majburiyat.                    ║
       * ║                                                                      ║
       * ║ Lekin egasi uni TOPOLMADI: «fond qaysi bo'limga qo'yildi, men        ║
       * ║ ko'rmadim». U fondni PUL sifatida o'ylaydi — «fond ochib qo'yamiz»,  ║
       * ║ «DDS da ehson degan xarajat» — va pulni Moliyadan izlaydi.          ║
       * ║                                                                      ║
       * ║ Ekran qayerda turishini uni IZLAYDIGAN odam belgilaydi, hisoblar    ║
       * ║ rejasidagi o'rni emas. Manzili ham allaqachon `/finance/charity`     ║
       * ║ edi — ya'ni menyu manzilning o'ziga ham zid edi.                     ║
       * ║                                                                      ║
       * ║ Kassalar yonida: ikkalasida ham pul chiqadi va ikkalasi ham kunlik   ║
       * ║ amal.                                                                ║
       * ╚══════════════════════════════════════════════════════════════════════╝
       */
      { id: 'charity-fund', labelKey: 'nav.charity', to: '/finance/charity', route: '/finance/charity', permission: 'charity.view' },
      /*
       * VALYUTA KURSLARI — `fxrate.edit`, `fxrate.override` EMAS.
       *
       * `POST /api/fx/daily-rate` aynan shu kodni talab qiladi.
       * `fxrate.override` boshqa narsa: u HUJJATDA kunlik kursdan chetlanish
       * (to'lov formasidagi qo'lda kurs) va u kurslar ekraniga tegishli emas.
       *
       * O'QISH (`GET /api/fx/daily-rate`) umuman ruxsat talab qilmaydi —
       * kurs har rolga ko'rinadi va u yuqori paneldagi vidjetda turadi.
       * Ya'ni bu band «kursni KO'RISH» uchun emas, «kursni QO'YISH» uchun,
       * va shuning uchun ruxsati yozuv darajasida.
       */
      { id: 'fx-rates', labelKey: 'nav.fxRates', to: '/fx-rates', route: '/fx-rates', permission: 'fxrate.edit' },
    ],
  },

  /*
   * ==========================================================================
   * HISOBOTLAR — RUXSAT KODLARI API BILAN AYNAN BIR XIL
   * ==========================================================================
   * Har band `reports.controller.ts` dagi `@RequirePermissions(...)` bilan
   * MAYDONMA-MAYDON mos. Bu ongli qaror: menyu API dan QAT'IYROQ bo'lsa,
   * foydalanuvchi ochiq hisobotni ko'rmay qoladi; YUMSHOQROQ bo'lsa, band
   * bosilganda 403 chiqadi. Ikkalasi ham yomon, lekin ikkinchisi kamroq
   * zarar keltiradi — shuning uchun nusxa AYNAN olinadi va farq bo'lsa u
   * SERVER tomonda tuzatiladi.
   *
   * ANIQLANGAN NOMUVOFIQLIKLAR (PERMISSION_MATRIX §3.6 vs API):
   *   • «Savdo» matritsada `warehouse` uchun yopiq, API esa `so.view` talab
   *     qiladi va omborchida u BOR (◐⁹ — yig'ishga tayyor buyurtmalar).
   *   • «Dead stock» matritsada `sales` uchun yopiq, API `stock.view` talab
   *     qiladi va sotuvchida u BOR (◐⁵).
   * Ikkalasi ham server tomonda hal qilinishi kerak; menyuda ularni qo'lda
   * torroq qilish ikkinchi haqiqat manbaini tug'diradi.
   *
   * ==========================================================================
   * GURUH DARVOZASI — `null`, VA BU ONGLI
   * ==========================================================================
   * `filterMenu` 3-qoidasi bo'sh guruhni O'ZI olib tashlaydi, ya'ni bironta
   * hisobot ochiq bo'lmagan foydalanuvchi «Hisobotlar» sarlavhasini umuman
   * ko'rmaydi. Guruhga bitta ruxsat qo'yish esa NOTO'G'RI bo'lardi: hisobotlar
   * to'plami rollarda kesishmaydi (omborchida `stock.view`, moliyachida
   * `cashaccount.view`), ya'ni «hammaga umumiy» bitta kod yo'q. Qaysi birini
   * tanlasak ham, kimningdir ochiq hisoboti menyudan yo'qolardi.
   *
   * `sales` roli bu guruhda uchta band ko'radi: qoldiq, savdo va o'z
   * mijozlarining qarzi. Bo'sh bo'lim unga hech qachon ko'rinmaydi.
   */
  {
    id: 'accounting',
    labelKey: 'nav.group.accounting',
    permission: 'journal.view',
    icon: 'accounting',
    items: [
      { id: 'acc-income-statement', labelKey: 'nav.accIncomeStatement', to: '/reports/income-statement', route: '/reports/income-statement', permission: 'accounting.report.view' },
      { id: 'acc-balance-sheet', labelKey: 'nav.accBalanceSheet', to: '/reports/balance-sheet', route: '/reports/balance-sheet', permission: 'accounting.report.view' },
      { id: 'acc-equity', labelKey: 'nav.accEquity', to: '/reports/equity', route: '/reports/equity', permission: 'accounting.report.view' },
      /*
       * AYLANMA-QOLDIQ — hisobotlardan KEYIN, jurnaldan OLDIN.
       *
       * O'qish tartibi shunday: natija (foyda, balans) → uning tarkibi
       * (vedomost) → alohida yozuv (jurnal). Vedomost aynan o'rtada turadi
       * va u ikki tomonni bog'laydigan hujjat.
       */
      { id: 'acc-trial-balance', labelKey: 'nav.accTrialBalance', to: '/accounting/trial-balance', route: '/accounting/trial-balance', permission: 'accounting.report.view' },
      /*
       * SVERKA — daftar operatsion tomon bilan mos keladimi.
       *
       * Bu hisobot EMAS, DIAGNOSTIKA: u raqam bermaydi, «raqamlarga ishonsa
       * bo'ladimi» degan savolga javob beradi. Shuning uchun vedomostdan
       * keyin turadi — avval o'qiladigan narsa, keyin uning ishonchliligi.
       */
      { id: 'acc-reconciliation', labelKey: 'nav.accReconciliation', to: '/accounting/reconciliation', route: '/accounting/reconciliation', permission: 'accounting.report.view' },
      { id: 'acc-journals', labelKey: 'nav.accJournals', to: '/accounting/journals', route: '/accounting/journals', permission: 'journal.view' },
      { id: 'acc-periods', labelKey: 'nav.accPeriods', to: '/accounting/periods', route: '/accounting/periods', permission: 'journal.view' },
      /*
       * QAYTA BAHOLASH — `fxreval.view`, `journal.view` EMAS.
       *
       * Kod AYNAN kontrollerdagi bilan bir xil
       * (`fx-revaluation.controller.ts` → `@RequirePermissions('fxreval.view')`).
       * Ikkalasi bugun bir xil uch rolda (admin, rahbar, moliyachi — seed'da
       * `fxreval.*` `ACCOUNTING_CODES` ro'yxatida), ya'ni farq ko'rinmaydi;
       * lekin ular bir kun ajralib ketadi va o'shanda band bosilganda 403
       * chiqardi (`rpt-sales` bandidagi xato tarixi).
       *
       * Nima uchun BUXGALTERIYA guruhida, «Moliya» da emas: amal daftarga
       * provodka yozadi va yopilgan davr raqamini o'zgartiradi. Guruh
       * darvozasi `journal.view` — u aynan shu uch rolda, ya'ni band
       * darvozadan o'tadi.
       *
       * Bandning o'rni «Davrlar» dan KEYIN va bu ish oqimi: qayta baholash
       * davrni yopishdan OLDINGI oxirgi qadam, ya'ni odam davrlar ekranidan
       * shu yerga qaraydi.
       */
      { id: 'acc-fx-revaluation', labelKey: 'nav.fxRevaluation', to: '/finance/fx-revaluation', route: '/finance/fx-revaluation', permission: 'fxreval.view' },
      { id: 'acc-accounts', labelKey: 'nav.accAccounts', to: '/settings/accounts', route: '/settings/accounts', permission: 'account.view' },
    ],
  },

  {
    id: 'reports',
    labelKey: 'nav.group.reports',
    permission: null,
    icon: 'reports',
    items: [
      /*
       * MARKAZ — guruhning BIRINCHI bandi.
       *
       * Bu guruhda 18 hisobot bor va ularning nomlari bir-biriga o'xshaydi
       * («Qoldiq», «Qoldiq qiymati», «Qoldiq yoshi», «ABC», «XYZ», «RFM»).
       * Yig'ilgan menyuda bu 18 qatorli ro'yxat bo'lib chiqadi va kerakli
       * bandni topish uchun ko'z hammasini o'qishi kerak.
       *
       * Markaz esa har hisobotni O'ZI javob beradigan savol bilan
       * ko'rsatadi va qidiruvi bor. Menyu bandlari QOLDIRILDI: kuniga
       * ochiladigan hisobotga ikki bosish o'rniga bir bosish qulayroq, va
       * markazga majburlash o'rganib qolgan odamni sekinlashtirardi.
       *
       * Ya'ni ikkala yo'l ham ochiq: bilgan odam to'g'ridan-to'g'ri
       * boradi, izlayotgan odam markazdan qaraydi.
       */
      { id: 'reports-hub', labelKey: 'nav.reportsHub', to: '/reports', route: '/reports', permission: null, onlyWithSiblings: true },
      { id: 'rpt-stock', labelKey: 'nav.reportStock', to: '/reports/stock', route: '/reports/stock', permission: 'stock.view' },
      { id: 'rpt-stock-value', labelKey: 'nav.reportStockValue', to: '/reports/stock-valuation', route: '/reports/stock-valuation', permission: 'stock.value.view' },
      /*
       * `report.sales.view`, `so.view` EMAS.
       *
       * Menyu API dan YUMSHOQROQ bo'lsa, foydalanuvchi band ko'radi va
       * bosganda 403 oladi — bu eng yomon holat: u nimadir buzilgan deb
       * o'ylaydi va qo'ng'iroq qiladi. Bu yerda aynan shunday edi:
       * `/reports/sales` xavfsizlik auditidan keyin `report.sales.view` ga
       * o'tkazildi (omborchi butun kompaniya tushumini ko'rardi), menyu esa
       * eski `so.view` da qolgan — omborchida u BOR.
       *
       * Qoida: menyu ruxsati kontroller ruxsati bilan AYNAN bir xil bo'lishi
       * kerak. Yumshoqroq bo'lsa — 403; qattiqroq bo'lsa — ochiq ekran
       * ko'rinmay qoladi.
       */
      { id: 'rpt-sales', labelKey: 'nav.reportSales', to: '/reports/sales', route: '/reports/sales', permission: 'report.sales.view' },
      /*
       * Agent faoliyati — `report.sales.view`, `so.view` EMAS.
       *
       * Endpoint (`sales-orders.controller.ts` → `GET /agent-activity`) aynan
       * shu kodni talab qiladi. `so.view` omborchida ham BOR, ya'ni u bilan
       * yozilsa omborchi butun savdo jamoasining faoliyat hisobotini ochardi.
       * Bitta HUJJATNI ko'rish huquqi AGREGATNI ko'rish huquqini keltirib
       * chiqarmaydi.
       *
       * JONLI TEKSHIRILGAN: `ombor@evo.uz` → 403; `sotuvchi@evo.uz` → 200,
       * lekin javobda FAQAT o'z satri (cheklov `ownAgentFilter()` da, ruxsatda
       * emas — shuning uchun menyuda alohida shart yo'q).
       */
      { id: 'rpt-agent-activity', labelKey: 'nav.reportAgentActivity', to: '/reports/agent-activity', route: '/reports/agent-activity', permission: 'report.sales.view' },
      // Yalpi foyda — `sales` uchun QAT'IY yopiq (§4: marja ko'rinmaydi).
      { id: 'rpt-profit', labelKey: 'nav.reportProfit', to: '/reports/gross-profit', route: '/reports/gross-profit', permission: 'so.margin.view' },
      /*
       * Chegirmalar marja hisoboti YONIDA: ikkalasi ham bitta savolga
       * javob beradi — «foydamiz qayerga ketyapti». Chegirma ko'rinmasa,
       * marjaning pasayishi sabab ko'rsatmasdan qolardi.
       *
       * Ruxsat ham AYNAN o'sha: `so.margin.view`. Chegirma foizi narx
       * siyosati va u sotuvchidan yashiriladi.
       */
      { id: 'rpt-discounts', labelKey: 'nav.discounts', to: '/reports/discounts', route: '/reports/discounts', permission: 'so.margin.view' },
      { id: 'rpt-ar-aging', labelKey: 'nav.reportArAging', to: '/reports/ar-aging', route: '/reports/ar-aging', permission: 'ar.view' },
      /*
       * MIJOZ INTIZOMI — AR aging dan KEYIN va bu tartib ma'noli.
       *
       * Aging «KIM yomon to'layapti?» degan savolga javob beradi va u
       * ro'yxat. Intizom esa AYNAN BITTA dilerni oyma-oy ochadi — ya'ni
       * u aging'dan keyingi qadam. Teskari tartibda qo'yilsa, foydalanuvchi
       * mijozni bilmasdan turib mijoz so'raydigan ekranga tushardi.
       */
      { id: 'rpt-discipline', labelKey: 'nav.reportDiscipline', to: '/reports/customer-discipline', route: '/reports/customer-discipline', permission: 'ar.view' },
      /*
       * ========================================================================
       * AP AGING BANDI OLIB TASHLANDI — VA BU IZOH UNING O'RNIDA QOLADI
       * ========================================================================
       * Bu yerda `rpt-ap-aging` bandi turardi va u «`reports.controller.ts` da
       * endpoint YO'Q» degan izoh bilan `route: null` da qolib ketgan edi.
       *
       * IZOH XATO EDI. Endpoint BOR:
       *   `@Get('ap-aging') @RequirePermissions('ap.view')` — reports.controller.ts
       *
       * JONLI TEKSHIRILGAN (bugun, 3000-port): `admin@evo.uz` →
       * `GET /api/reports/ap-aging` 200 va ikkita ta'minotchi qatori
       * (`apOutstandingBase: "272803.2754"`); `ombor@evo.uz` va
       * `sotuvchi@evo.uz` → 403.
       *
       * Ekran YOZILDI, lekin `/reports/ap-aging` da emas — `/payables` da,
       * «Moliya» bo'limida. Sabab `reorder-runs` bilan bir xil: ta'minotchi
       * qarzi OCHIB YOPILADIGAN hisobot emas, kundalik ish ro'yxati —
       * «kimga qancha qarzmiz, qaysi avans osilib qoldi». Uning o'rni
       * mijoz qarzlari va to'lovlar yonida.
       *
       * Ikkita band (Moliyada va Hisobotlarda) bitta manzilga ko'rsatilmadi:
       * `findActiveMenuItem()` eng uzun mos manzilni tanlaydi va ikki band
       * bir xil `route` bilan turganda qaysi biri yoritilishi TASODIFGA
       * qolardi — foydalanuvchi esa o'zini boshqa bo'limda deb o'ylardi.
       */
      { id: 'rpt-cash-flow', labelKey: 'nav.reportCashFlow', to: '/reports/cash-flow', route: '/reports/cash-flow', permission: 'cashaccount.view' },
      /*
       * `stock.detail.view` — kontroller `stock.view` VA `stock.detail.view`
       * ni birga talab qiladi (guard AND). Sotuvchida birinchisi bor,
       * ikkinchisi yo'q; menyu faqat birinchisiga qarab bandni ochib
       * qo'yardi va sotuvchi 403 olardi.
       *
       * Ikkalasini yozish shart emas: `stock.detail.view` bor odamda
       * `stock.view` ham bor (seed matritsasi), ya'ni torroq kod yetarli.
       */
      { id: 'rpt-dead-stock', labelKey: 'nav.reportDeadStock', to: '/reports/dead-stock', route: '/reports/dead-stock', permission: 'stock.detail.view' },
      // Konteyner rentabelligi — SKU tannarx tarkibiga (`/reports/landed-cost/$id`)
      // kirish nuqtasi ham shu. U alohida band emas: konkret yuk talab qiladi.
      { id: 'rpt-shipment-profit', labelKey: 'nav.reportShipmentProfit', to: '/reports/shipment-profitability', route: '/reports/shipment-profitability', permission: 'landedcost.view' },
      /*
       * «Qachon buyurtma berish» — GAP_ANALYSIS §A4. Ruxsat `po.view`,
       * chunki bu OMBOR holati emas, XARID qarori: lead time 120-150 kun
       * bo'lganda «tovar tugadi → buyurtma beraman» to'rt oylik uzilish
       * demak. Sotuvchida `po.view` yo'q — unga band ko'rinmaydi.
       */
      { id: 'rpt-reorder', labelKey: 'nav.reportReorder', to: '/reports/reorder', route: '/reports/reorder', permission: 'po.view' },

      /*
       * ========================================================================
       * TAHLIL HISOBOTLARI — TZ v4.0 §28 (CR-007), v8.0 §38–39
       * ========================================================================
       * Yuqoridagilar FAKTNI ko'rsatadi («qancha sotdik»), quyidagilar XULOSA
       * chiqaradi («qaysi mijoz foydali»). Ular alohida guruhga ajratilmadi:
       * foydalanuvchi «hisobot» so'zi bilan qidiradi va ikkinchi «Tahlil»
       * bo'limi uni ikki joyga qarashga majbur qilardi.
       *
       * HAR BAND RUXSATI KONTROLLER BILAN AYNAN BIR XIL
       * (`analytics.controller.ts`) — bu faylning boshidagi `rpt-sales`
       * tarixidan chiqqan qoida: menyu yumshoqroq bo'lsa 403, qattiqroq
       * bo'lsa ochiq ekran ko'rinmay qoladi.
       *
       *   ABC              `stock.value.view`   (tushum + KAPITAL qarori)
       *   XYZ              `stock.detail.view`  (pul yo'q — ombor qarori)
       *   RFM              `customer.view`      (+ `report.sales.view`, pastda)
       *   LTV              `so.margin.view`
       *   Ta'minotchi      `supplier.view`
       *   Konteyner        `landedcost.view`
       *   Ombor yoshi      `stock.detail.view`
       *   Mijoz foydaliligi `so.margin.view`
       */
      { id: 'rpt-abc', labelKey: 'nav.reportAbc', to: '/reports/abc', route: '/reports/abc', permission: 'stock.value.view' },
      { id: 'rpt-xyz', labelKey: 'nav.reportXyz', to: '/reports/xyz', route: '/reports/xyz', permission: 'stock.detail.view' },
      /*
       * RFM endpoint IKKI ruxsat talab qiladi (guard AND): `customer.view`
       * VA `report.sales.view`. Menyu bandi bittasini oladi va bu TORROQ
       * bo'lishi kerak. Bugun `customer.view` sotuvchida ham bor, omborchida
       * esa YO'Q — ya'ni u ikkalasidan torroq va omborchiga band ko'rinmaydi.
       * Sotuvchida ikkalasi ham bor, ya'ni u 403 olmaydi (natija esa
       * `ownSalesFilter()` bilan faqat o'z mijozlari).
       */
      { id: 'rpt-rfm', labelKey: 'nav.reportRfm', to: '/reports/rfm', route: '/reports/rfm', permission: 'customer.view' },
      { id: 'rpt-ltv', labelKey: 'nav.reportLtv', to: '/reports/ltv', route: '/reports/ltv', permission: 'so.margin.view' },
      { id: 'rpt-supplier-score', labelKey: 'nav.reportSupplierScore', to: '/reports/supplier-scorecard', route: '/reports/supplier-scorecard', permission: 'supplier.view' },
      { id: 'rpt-container', labelKey: 'nav.reportContainer', to: '/reports/container-efficiency', route: '/reports/container-efficiency', permission: 'landedcost.view' },
      { id: 'rpt-stock-age', labelKey: 'nav.reportStockAge', to: '/reports/stock-age', route: '/reports/stock-age', permission: 'stock.detail.view' },
      { id: 'rpt-customer-profit', labelKey: 'nav.reportCustomerProfit', to: '/reports/customer-profitability', route: '/reports/customer-profitability', permission: 'so.margin.view' },
    ],
  },

  /*
   * ==========================================================================
   * BUXGALTERIYA — GURUH DARVOZASI `journal.view`
   * ==========================================================================
   * PERMISSION_MATRIX: `journal.view` admin, rahbar va moliyachida bor;
   * sotuvchi va omborchida YO'Q. Ya'ni butun bo'lim ularga ko'rinmaydi va
   * bu to'g'ri: daftar kompaniyaning butun moliyaviy holatini ochadi.
   *
   * JONLI TEKSHIRILGAN: `sotuvchi@evo.uz` va `ombor@evo.uz` uchun
   * `GET /api/accounting/periods`, `/journals`, `/trial-balance` va uchala
   * moliyaviy hisobot ham 403; `moliya@evo.uz` va `rahbar@evo.uz` uchun 200.
   *
   * ==========================================================================
   * BAND RUXSATLARI KONTROLLER BILAN AYNAN BIR XIL
   * ==========================================================================
   *   Provodkalar   `journal.view`            (GET /accounting/journals)
   *   Davrlar       `journal.view`            (GET /accounting/periods)
   *   Hisoblar reja `account.view`            (GET /accounting/accounts)
   *   Uch hisobot   `accounting.report.view`  (GET /accounting/reports/*)
   *
   * «Davrlar» bandi `period.close` GA BOG'LANMAGAN va bu ongli: band
   * EKRANNI ochadi, ekran esa avval RO'YXATNI yuklaydi (`journal.view`).
   * Yopish tugmasi va tekshiruv ro'yxati EKRAN ICHIDA `period.close` bilan
   * tekshiriladi. Aks holda rahbar (unda `period.close` YO'Q) davrlar
   * holatini umuman ko'ra olmasdi — holbuki «oy yopildimi?» degan savol
   * aynan uniki.
   *
   * Uchta hisobot Hisobotlar guruhida EMAS, shu yerda: ular bir-birini
   * tushuntiradi (balansdagi «yopilmagan natija» → P&L, kapitaldagi «davr
   * natijasi» → P&L) va ular orasida yurish kundalik ish. Hisobotlar
   * guruhida ular savdo kesimlari orasida yo'qolib ketardi.
   */
  {
    id: 'loyalty',
    labelKey: 'nav.group.loyalty',
    permission: 'loyalty.view',
    icon: 'loyalty',
    items: [
      { id: 'loyalty-status', labelKey: 'nav.loyaltyStatus', to: '/loyalty', route: '/loyalty', permission: 'loyalty.view' },
      { id: 'loyalty-point-rules', labelKey: 'nav.loyaltyPointRules', to: '/loyalty/point-rules', route: '/loyalty/point-rules', permission: 'loyalty.view' },
      { id: 'loyalty-qr-batches', labelKey: 'nav.loyaltyQrBatches', to: '/loyalty/qr-batches', route: '/loyalty/qr-batches', permission: 'loyalty.view' },
      { id: 'loyalty-rewards', labelKey: 'nav.loyaltyRewards', to: '/loyalty/rewards', route: '/loyalty/rewards', permission: 'loyalty.view' },
      { id: 'loyalty-redemptions', labelKey: 'nav.loyaltyRedemptions', to: '/loyalty/redemptions', route: '/loyalty/redemptions', permission: 'loyalty.view' },
      { id: 'loyalty-installers', labelKey: 'nav.loyaltyInstallers', to: '/loyalty/installers', route: '/loyalty/installers', permission: 'loyalty.view' },
      { id: 'loyalty-fraud', labelKey: 'nav.loyaltyFraud', to: '/loyalty/fraud', route: '/loyalty/fraud', permission: 'loyalty.fraud.view' },
      /*
       * ╔══════════════════════════════════════════════════════════════════╗
       * ║ DILLER VA BONUS SHU GURUHDA — VA GURUH DARVOZASI MUAMMOSI        ║
       * ║                                                                  ║
       * ║ Uch band `dealer.view` bilan, guruh darvozasi esa `loyalty.view`.║
       * ║ Ya'ni `dealer.view` bor-u `loyalty.view` yo'q rol bu bandlarni   ║
       * ║ KO'RMAYDI. Bugungi matritsada bunday rol YO'Q: `dealer.view`     ║
       * ║ admin, rahbar va moliyachida, `loyalty.view` ham AYNAN o'shalarda║
       * ║ (seed/permissions.ts) — ya'ni to'plamlar ustma-ust tushadi.      ║
       * ║                                                                  ║
       * ║ Ular ajralgan kuni bu bandlar alohida guruhga chiqarilishi kerak.║
       * ║ Bugun alohida guruh ochish esa noto'g'ri bo'lardi: bonus BITTA   ║
       * ║ daftarda (`bonus_entries`) va usta bali bilan diller rebate'i    ║
       * ║ YONMA-YON ko'riladi — ularni ikki bo'limga ajratish «bonus       ║
       * ║ dasturi bizga qancha turdi?» savolini ikki ekranga bo'lardi.     ║
       * ╚══════════════════════════════════════════════════════════════════╝
       *
       * YARATISH va TO'LASH ruxsatlari (`dealer.manage`, `dealer.rebate.manage`,
       * `dealer.bonus.payout`) menyuda YO'Q — ular EKRAN ICHIDA tekshiriladi
       * (yuqoridagi `loyalty.*` bandlari bilan bir xil qoida va bir xil sabab:
       * bandning vazifasi ekranni ochish, ekran esa RO'YXATNI yuklaydi va u
       * `dealer.view` talab qiladi).
       */
      { id: 'dealers', labelKey: 'nav.dealers', to: '/dealers', route: '/dealers', permission: 'dealer.view' },
      /*
       * NAZORAT — diller ro'yxatining YONIDA va o'sha ruxsat ostida
       * (`dealer.view`). Alohida ruxsat o'ylab topilmadi: nazorat jadvali
       * o'sha ro'yxatning boshqa kesimi, yangi qaror bermaydi.
       *
       * Egasi: «asosan biz nazoratda bo'lishimiz uchun, chunki qarzdorlik
       * katta turadi — 200–300 ming dollar nasiyaga ishlaydi».
       */
      { id: 'dealer-control', labelKey: 'nav.dealerControl', to: '/dealers/control', route: '/dealers/control', permission: 'dealer.view' },
      { id: 'bonus-accounts', labelKey: 'nav.bonusAccounts', to: '/bonus', route: '/bonus', permission: 'dealer.view' },
      { id: 'bonus-ledger', labelKey: 'nav.bonusLedger', to: '/bonus/entries', route: '/bonus/entries', permission: 'dealer.view' },
    ],
  },

  /*
   * XODIMLAR — komissiya, KPI, bonus (TZ v8.0 §25, §19).
   *
   * ==========================================================================
   * GURUH DARVOZASI `commission.view` — VA `sales` UNI KO'RMAYDI
   * ==========================================================================
   * Agent o'z komissiyasini `GET /api/commission/my` orqali ko'radi, ya'ni
   * BOSHQA joyda — bu bo'lim esa BARCHA xodimlar kesimidagi hisob va uni
   * agent ko'rmasligi kerak. Guruhga `commission.view` qo'yilishi shuni
   * ta'minlaydi: `sales` va `warehouse` butun bo'limni umuman ko'rmaydi.
   *
   * `usta` (loyalty) bo'limidan farqi shu yerda ko'rinadi: usta — TASHQI
   * shaxs va uning ballari marketing katalogi; agent esa XODIM va uning
   * komissiyasi oylik. Ikkalasini bitta guruhga qo'yish ularni bir xil
   * tushuncha deb ko'rsatardi.
   *
   * Nishon `finance` (hamyon) — komissiya pul masalasi. Alohida «xodim»
   * nishoni `components/layout/nav-icons.tsx` da yo'q va u fayl bu
   * vazifaning chegarasidan tashqarida; mavjud nishonni ma'nosiga ko'ra
   * qayta ishlatish yangi nishon o'ylab topishdan yaxshiroq.
   */
  {
    id: 'master-data',
    labelKey: 'nav.group.masterData',
    permission: null,
    icon: 'catalog',
    items: [
      { id: 'products', labelKey: 'nav.products', to: '/products', route: '/products', permission: 'product.view' },
      /*
       * KATEGORIYA VA BRENDLAR — mahsulotdan KEYIN turadi (2026-08-15).
       *
       * Tartib ish oqimini takrorlaydi: operator avval katalogni ochadi,
       * kerakli brend tanlagichda yo'qligini KO'RADI va shundan keyin uni
       * qo'shishga o'tadi. Menyuning boshiga qo'yilsa, u har kuni ochiladigan
       * «Mahsulotlar» dan yuqorida turgan, lekin oyda bir marta kerak
       * bo'ladigan band bo'lardi.
       *
       * Ruxsat `product.view` — kontroller ham shunday (`categories`,
       * `brands`, `uoms` GET lari aynan shu kod bilan yopilgan).
       */
      { id: 'reference', labelKey: 'nav.reference', to: '/reference', route: '/reference', permission: 'product.view' },
      { id: 'customers', labelKey: 'nav.customers', to: '/customers', route: '/customers', permission: 'customer.view' },
      /*
       * TA'MINOTCHILAR — IKKI RUXSAT, CHUNKI KONTROLLER HAM SHUNDAY.
       *
       * `suppliers.controller.ts`:
       *   `@RequireAnyPermission('supplier.view', 'supplier.contact.view')`
       *
       * Ya'ni ekran ikki xil kenglikda ochiladi va IKKALASI ham haqiqiy ish:
       * moliyachi/rahbar tijorat shartlarini ko'radi, omborchi esa faqat nom
       * va kontaktni — konteyner kelganda «kimga qo'ng'iroq qilaman?» degan
       * savol aynan uniki (MATRIX §3.1 izoh³).
       *
       * Faqat `supplier.view` yozilsa, omborchi ochiq ekranni menyuda
       * ko'rmasdi; faqat `supplier.contact.view` yozilsa, MOLIYACHI ko'rmay
       * qolardi — unda bu kod YO'Q (seed: `FINANCE_PERMISSIONS`). Shuning
       * uchun ikkalasi ham yoziladi.
       *
       * `sales` esa ikkalasidan ham mahrum — TZ §60: sotuvchi ta'minotchi
       * narxini ko'rmaydi. JONLI TEKSHIRILGAN: `sotuvchi@evo.uz` →
       * `GET /api/suppliers` 403; `ombor@evo.uz` → 200, lekin javobda
       * `defaultCurrency`/`paymentTermDays`/`leadTimeDays` KALITLARI YO'Q.
       */
      {
        id: 'suppliers',
        labelKey: 'nav.suppliers',
        to: '/suppliers',
        route: '/suppliers',
        permission: 'supplier.view',
        orPermission: 'supplier.contact.view',
      },
      // Narx ro'yxati SOZLAMALAR ekranida turadi, lekin menyudagi o'rni shu
      // yerda: sotuvchi uni ma'lumotnoma sifatida ochadi (`pricelist.view`
      // unda BOR — jonli tekshirilgan: `/price-lists` sotuvchiga 200).
      { id: 'pricelists', labelKey: 'nav.pricelists', to: '/settings/price-lists', route: '/settings/price-lists', permission: 'pricelist.view' },
    ],
  },

  /*
   * ==========================================================================
   * OMBOR — RUXSAT KODLARI KONTROLLER BILAN AYNAN BIR XIL
   * ==========================================================================
   * Har band AYNAN o'sha ekran chaqiradigan endpointning ruxsatini oladi:
   *
   *   Qoldiq / Daftar        `stock.view`     (stock.controller.ts)
   *   Kirim / Chiqim         `receipt.create` (movements.controller.ts — chiqim
   *                                            uchun ALOHIDA `issue.create` YO'Q:
   *                                            beradigan ham, olib qo'yadigan ham
   *                                            bitta rol — omborchi)
   *   Ko'chirish / Sanoq     `receipt.view`   (inventory-queries.controller.ts)
   *
   * ==========================================================================
   * KO'CHIRISH VA SANOQ — `receipt.view`, `transfer.create` EMAS
   * ==========================================================================
   * Bandning vazifasi — EKRANNI ochish, ekran esa avval RO'YXATNI yuklaydi.
   * Ro'yxat endpointi `receipt.view` talab qiladi, yaratish esa
   * `transfer.create`/`count.create`. Bandni yaratish ruxsatiga bog'lasak,
   * moliyachi (`receipt.view` ✓, `transfer.create` —) hujjatlarni umuman
   * ko'ra olmasdi — hujjatlarni KO'RISH esa aynan uning ishi.
   *
   * Teskari xato ham yo'q: sotuvchida `receipt.view` YO'Q, ya'ni bu ikki band
   * unga ko'rinmaydi va endpoint ham 403 qaytaradi (jonli tekshirilgan).
   * Yaratish va tasdiqlash tugmalari EKRAN ICHIDA o'z ruxsatini tekshiradi.
   *
   * Guruh darvozasi `stock.view`: sotuvchida u BOR (§3.2 izoh⁵ — faqat
   * «bor/yo'q»), shuning uchun u «Qoldiq» va «Daftar» ni ko'radi, qolgani
   * unga yopiq.
   */
  {
    /*
     * XODIMLAR — bo'limning hammasi shu yerda.
     *
     * Egasi: «u bo'limni xodimlar ichiga yig'asan». Ilgari bu guruhda
     * faqat komissiya va KPI turardi va ular «bu oyda kimga qancha
     * hisoblandi» degan savolga javob berardi. Endi «bu odam kim va u
     * bilan nima shartlashganmiz» ham shu yerda — birinchi band bo'lib,
     * chunki qolgan hammasi shu kartaga tayanadi.
     *
     * Guruh darvozasi `employee.view` ga o'zgartirildi: xodimlar ro'yxati
     * endi bo'limning asosiy ekrani va komissiya undan keyin keladi.
     */
    id: 'staff',
    labelKey: 'nav.staff',
    permission: 'employee.view',
    icon: 'finance',
    items: [
      { id: 'employees', labelKey: 'nav.employees', to: '/hr/employees', route: '/hr/employees', permission: 'employee.view' },
      { id: 'payroll', labelKey: 'nav.payroll', to: '/hr/payroll', route: '/hr/payroll', permission: 'payroll.view' },
      /*
       * Nazorat bo'limi menyuda `payroll.view` ostida: jadvalda pul ham
       * bor va xizmat IKKALA huquqni talab qiladi. Faqat `employee.view`
       * bo'lgan foydalanuvchiga bandni ko'rsatib, keyin 403 berish —
       * eng yomon variant: u nimadir yashirilgan deb o'ylaydi.
       */
      { id: 'hr-control', labelKey: 'nav.control', to: '/hr/control', route: '/hr/control', permission: 'payroll.view' },
      { id: 'commission', labelKey: 'nav.commission', to: '/hr/commission', route: '/hr/commission', permission: 'commission.view' },
      { id: 'kpi', labelKey: 'nav.kpi', to: '/hr/kpi', route: '/hr/kpi', permission: 'kpi.view' },
    ],
  },

  /*
   * ╔══════════════════════════════════════════════════════════════════════╗
   * ║ XAVFSIZLIK — O'Z BO'LIMI, «TIZIM» NING ICHIDA EMAS                   ║
   * ║                                                                      ║
   * ║ Egasining talabi: «Savdoda, pul tushumida, pul chiqimda va hokazo    ║
   * ║ operatsiyada o'zgarish qilinsa, o'chirilsa — o'shani BITTA BO'LIM    ║
   * ║ qilasan, uning ichida barchasi turadi». Va: «barchasi 1 joyda        ║
   * ║ tursin — barcha holat nazoratda turishi uchun».                      ║
   * ║                                                                      ║
   * ║ Ilgari ekran «Tizim» guruhining uchinchi bandi edi — foydalanuvchi,  ║
   * ║ rol, chop etish shabloni va tashkiliy tuzilma orasida. Ya'ni u       ║
   * ║ SOZLAMA sifatida ko'rinardi, aslida esa u sozlama emas: bu yerda     ║
   * ║ hech narsa o'zgartirilmaydi, faqat BO'LIB O'TGAN ish o'qiladi.       ║
   * ║                                                                      ║
   * ║ GURUH DARVOZASI BANDNING O'Z RUXSATI BILAN BIR XIL (`audit.view`).   ║
   * ║ Bu fayldagi qoida bo'yicha odatda bu yaxshi belgi emas (guruhda      ║
   * ║ bitta band), lekin bu yerda u AYNAN to'g'ri — `supply` guruhidagi    ║
   * ║ bilan bir xil sabab: bo'lim butunligicha ochiladi yoki umuman        ║
   * ║ ochilmaydi. Uni «Tizim» darvozasi ostida qoldirish HAM shu kodni     ║
   * ║ talab qilardi (o'sha guruh ham `audit.view` bilan yopiq), ya'ni      ║
   * ║ ko'rinadigan rollar to'plami ZARRACHA o'zgarmadi: admin, rahbar,     ║
   * ║ moliyachi.                                                           ║
   * ║                                                                      ║
   * ║ O'RNI — «Tizim» dan OLDIN. Bu fayldagi tartib qoidasi «kuniga necha  ║
   * ║ marta ochiladi?» degan savolga tayanadi. Xavfsizlik jurnali          ║
   * ║ sozlamalardan tez-tez ochiladi (savol tug'ilgan zahoti), lekin       ║
   * ║ savdo va ombordan kamroq — shuning uchun u uchinchi qavatning        ║
   * ║ boshida turadi, birinchisida emas.                                   ║
   * ║                                                                      ║
   * ║ ⚠️ BAND VA GURUH BIR XIL NOM BILAN ATALADI — VA BU ATAYLAB.          ║
   * ║                                                                      ║
   * ║ `sidebar.tsx` da qoida bor: guruhda BITTA band qolsa, u yig'iladigan ║
   * ║ guruh emas, TO'G'RIDAN-TO'G'RI HAVOLA bo'lib chiziladi va sarlavha   ║
   * ║ sifatida BANDNING nomi olinadi (guruh nomi emas — «Moliya» bosib     ║
   * ║ «Kassa hisoblari» ga tushish chalg'itadi degan sabab bilan).         ║
   * ║                                                                      ║
   * ║ Ya'ni bandga boshqa nom («Hodisalar jurnali») berilsa, yon menyuda   ║
   * ║ «Xavfsizlik» degan so'z UMUMAN ko'rinmasdi — egasi tanlagan bo'lim   ║
   * ║ nomi ekranga chiqmasdi. Jonli o'lchangan: guruh tugmalari ro'yxatida ║
   * ║ «Xavfsizlik» yo'q edi, o'rnida `/security` havolasi turardi.         ║
   * ║                                                                      ║
   * ║ Ikkinchi band qo'shilgan kuni guruh yig'iladigan bo'ladi va o'shanda ║
   * ║ bandga o'z nomi beriladi — sarlavha baribir «Xavfsizlik» bo'lib      ║
   * ║ qoladi.                                                              ║
   * ╚══════════════════════════════════════════════════════════════════════╝
   */
  {
    id: 'security',
    labelKey: 'nav.group.security',
    permission: 'audit.view',
    icon: 'security',
    items: [
      {
        id: 'security-log',
        labelKey: 'nav.group.security',
        to: '/security',
        route: '/security',
        permission: 'audit.view',
      },
    ],
  },

  {
    id: 'system',
    labelKey: 'nav.group.system',
    permission: 'audit.view',
    icon: 'system',
    items: [
      { id: 'users', labelKey: 'nav.users', to: '/settings/users', route: '/settings/users', permission: 'user.view' },
      // Rol → ruxsat matritsasi faqat O'QISH uchun, shuning uchun ruxsat ham
      // o'qish darajasida: `user.view` (admin va rahbar).
      { id: 'roles', labelKey: 'nav.roles', to: '/settings/roles', route: '/settings/roles', permission: 'user.view' },
      /*
       * TASDIQLASH SOZLAMALARI — navbatdan farqli, bular haqiqiy sozlama.
       *
       * `approval.rule.edit` moliyachida ATAYLAB yo'q (seed izohi: u
       * tasdiqlaydi, lekin «men qancha summagacha tasdiqlay olaman»
       * chegarasini o'zi yozmasligi kerak) — ya'ni band unga ko'rinmaydi.
       *
       * Vakolat berish esa `approval.view`: ta'tilga chiqayotgan moliyachi
       * O'Z imzo huquqini o'zi bera olishi kerak, administratorni kutib
       * o'tirmasdan. Begona vakolatni yozish serverda `approval.delegate`
       * bilan yopilgan — menyuda emas, chunki bu bandning ikkita ma'nosi
       * bor va ulardan biri hammaga ochiq.
       */
      { id: 'approval-rules', labelKey: 'nav.approvalRules', to: '/settings/approval-rules', route: '/settings/approval-rules', permission: 'approval.rule.edit' },
      { id: 'delegations', labelKey: 'nav.delegations', to: '/settings/delegations', route: '/settings/delegations', permission: 'approval.view' },
      /*
       * MENING SOZLAMALARIM — ruxsat TALAB QILINMAYDI (`permission: null`).
       *
       * Server ham shunday: har rol o'z Telegramini ulay olishi kerak.
       * Haydovchi ham, omborchi ham xabar oladi — ular tizimning eng ko'p
       * xabar keladigan foydalanuvchilari.
       *
       * Bu band bo'lmagani uchun bazada NOLTA ulanish bor edi: butun
       * bildirishnoma quvuri ishlab, hech kimga yetib bormasdi.
       */
      { id: 'my-settings', labelKey: 'nav.mySettings', to: '/settings/me', route: '/settings/me', permission: null },
      { id: 'settings', labelKey: 'nav.systemSettings', to: '/settings/system', route: '/settings/system', permission: 'settings.edit' },
      /*
       * CHOP ETISH SHABLONLARI — `settings.edit`, API bilan AYNAN bir xil.
       *
       * Ekran mijozga ketadigan blankaning MATNINI o'zgartiradi: rekvizit,
       * sarlavha, izoh. Bu «ko'rish» emas va o'qish ruxsati bilan ochilishi
       * mumkin emas (tashkiliy tuzilma bandidagi bilan bir xil mulohaza).
       */
      { id: 'print-templates', labelKey: 'nav.printTemplates', to: '/settings/print-templates', route: '/settings/print-templates', permission: 'settings.edit' },
      /*
       * TASHKILIY TUZILMA — `settings.edit`, ya'ni FAQAT admin.
       *
       * Ruxsat API bilan aynan bir xil (`org-admin.controller.ts`). `user.manage`
       * emas: u rahbarda ham bor va menyu API dan yumshoqroq bo'lardi — rahbar
       * bandni ko'rib, bosgach 403 olardi.
       *
       * Nima uchun `user.view` ham emas: bu ekran filial OCHADI, ya'ni yozish
       * amali. O'qish ruxsati bilan yozish ekranini ko'rsatish — menyuning eng
       * klassik xatosi.
       */
      { id: 'organization', labelKey: 'nav.organization', to: '/settings/organization', route: '/settings/organization', permission: 'settings.edit' },
      /*
       * PARTNER TASHKILOTLAR — `tenant.view`, kontroller bilan AYNAN bir xil
       * (`@RequirePermissions('tenant.view')`). Yaratish va paketni
       * o'zgartirish tor kodlar ostida (`tenant.create`,
       * `tenant.modules.manage`), lekin band RO'YXATNI ochadi — menyu
       * API dan qattiqroq bo'lmasligi kerak.
       */
      { id: 'partner-orgs', labelKey: 'nav.partnerOrgs', to: '/settings/organizations', route: '/settings/organizations', permission: 'tenant.view' },
    ],
  },
];

/**
 * Menyuni foydalanuvchi ruxsatlari bo'yicha filtrlaydi.
 *
 * Qoida:
 *   1. Guruh darvozasi yopiq bo'lsa — butun guruh tushib qoladi.
 *   2. Band ruxsati yo'q bo'lsa — band tushib qoladi.
 *   3. Guruhda bironta band qolmasa — guruh sarlavhasi ham chiqmaydi
 *      (bo'sh «Moliya» sarlavhasi «nimadir bor, lekin menga yopiq» degan
 *      ma'lumot beradi — bu ham ortiqcha).
 *
 * `display:none` ISHLATILMAYDI: PERMISSION_MATRIX §4.2 3-qatlam talabi —
 * yashirilgan element DOM'da qolmasligi kerak.
 */
/**
 * Joriy manzilga mos keladigan band va uning guruhi.
 *
 * Eng UZUN mos keluvchi manzil bo'yicha: `/loyalty/point-rules` ham
 * `/loyalty` ga, ham o'ziga mos keladi va aniqrog'i tanlanishi kerak.
 * Bosh sahifa (`/`) barcha manzillarning prefiksi, shuning uchun u faqat
 * AYNAN teng kelganda hisobga olinadi.
 *
 * Ikki joyda kerak: yon menyu qaysi guruhni ochiq tutishini, yuqori panel
 * esa sahifa sarlavhasini shundan oladi. Mantiq bitta bo'lishi SHART —
 * aks holda menyuda «Ombor» yoritilgan turib, sarlavhada «Savdo» yozilishi
 * mumkin va bu foydalanuvchini chalg'itadi.
 */
export function findActiveMenuItem(
  groups: readonly MenuGroup[],
  pathname: string,
): { group: MenuGroup; item: MenuItem } | null {
  let best: { group: MenuGroup; item: MenuItem; length: number } | null = null;

  for (const group of groups) {
    for (const item of group.items) {
      if (item.route === null) continue;
      const matches = item.route === '/' ? pathname === '/' : pathname.startsWith(item.route);
      if (matches && (best === null || item.route.length > best.length)) {
        best = { group, item, length: item.route.length };
      }
    }
  }

  return best === null ? null : { group: best.group, item: best.item };
}

export function filterMenu(
  groups: readonly MenuGroup[],
  can: (permission: string) => boolean,
): MenuGroup[] {
  const allowed = (permission: string | null): boolean => permission === null || can(permission);

  /*
   * Band darvozasi: `permission` YOKI `orPermission`.
   *
   * Guruh darvozasida bunday muqobil YO'Q va u kerak ham emas: guruh
   * darvozasi butun bo'limni yopadi va u har doim BITTA umumiy kod
   * (`stock.view`, `po.view`, `cashaccount.view`). Agar bir kun guruhga ham
   * OR kerak bo'lsa, bu «guruh noto'g'ri bo'lingan» degan belgi bo'ladi.
   */
  const itemAllowed = (item: MenuItem): boolean =>
    allowed(item.permission) || (item.orPermission !== undefined && can(item.orPermission));

  return groups
    .filter((group) => allowed(group.permission))
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          itemAllowed(item) &&
          /*
           * TAYYOR BO'LMAGAN EKRANLAR MENYUDA KO'RSATILMAYDI.
           *
           * Ilgari ular ko'rinardi (bosilmaydigan, xira matn) — mulohaza
           * «fazalar rejasi ko'zga tashlanib tursin» edi. Bu DASTURCHINING
           * qulayligi, foydalanuvchining emas: 44 banddan 11 tasi bosilmaydi
           * va menyu shu qadar uzayadiki, ishlaydigan bandni topish qiyin
           * bo'ladi. Egasining birinchi mulohazasi ham aynan shu bo'ldi —
           * «bo'limlar uzun qator bo'lib ketgan».
           *
           * Reja hujjatda (`EVO_MVP_PLAN_v1.md`) va shu fayldagi
           * `route: null` da qoladi — u yerda uning o'rni bor.
           */
          item.route !== null &&
          item.hidden !== true,
      ),
    }))
    /*
     * `onlyWithSiblings` — yig'uvchi bandlar (masalan «Hisobotlar markazi»).
     *
     * Ular guruhda YAKKA qolsa olib tashlanadi: markaz o'zi ma'lumot
     * ko'rsatmaydi, u qolgan bandlarni yig'adi — yig'adigan narsa
     * bo'lmasa, u bo'sh sahifaga olib boradigan band bo'lib qolardi.
     */
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          item.onlyWithSiblings !== true ||
          group.items.some((other) => other.id !== item.id && other.onlyWithSiblings !== true),
      ),
    }))
    .filter((group) => group.items.length > 0);
}
